// Reusable, game-agnostic spectator canvas: reflects a live drawing relayed for
// an interaction window. Drop into any draw game's host/detective
// screens. It only renders the stream — no input, no toolbar.
//
// The aspectRatio MUST match the artist's DrawingCanvas: strokes are stored in a
// shared logical space (LOGICAL_WIDTH wide, LOGICAL_WIDTH / aspectRatio tall), so
// a mismatched ratio would distort the mirrored drawing.

import React, { useCallback, useEffect, useRef } from 'react';
import { LOGICAL_WIDTH } from '@/components/drawing/constants';
import { CANVAS_BACKGROUND_COLOR } from '@/components/drawing/exportDrawing';
import { renderStrokes } from '@/components/drawing/renderStrokes';
import { cn } from '@/utilities/cn';
import { useDrawSpectatorRelay } from './useDrawRelay';

interface DrawingMirrorProps {
  roomCode: string;
  windowId: string;
  /** The artist whose canvas to mirror (canvases are per-artist). */
  playerId: string;
  /** Must match the artist canvas's aspectRatio (1, 4/3, 16/9, ...) */
  aspectRatio?: number;
  /**
   * Render only one edge of the mirrored canvas. left/right crop the width;
   * top/bottom crop the height (for vertically-stacked neighbors). Defaults to
   * the full canvas.
   */
  cropSide?: 'left' | 'right' | 'top' | 'bottom';
  /** Fraction of the cropped axis (width for left/right, height for top/bottom). */
  cropFraction?: number;
  className?: string;
}

export const DrawingMirror: React.FC<DrawingMirrorProps> = ({
  roomCode,
  windowId,
  playerId,
  aspectRatio = 4 / 3,
  cropSide,
  cropFraction = 1,
  className,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const state = useDrawSpectatorRelay({ roomCode, windowId, playerId });
  const logicalHeight = LOGICAL_WIDTH / aspectRatio;
  const normalizedCropFraction =
    cropSide && Number.isFinite(cropFraction)
      ? Math.min(1, Math.max(0.05, cropFraction))
      : 1;
  // The visible logical window: one axis is cropped to the fraction, anchored
  // to the requested edge; the other stays full.
  const croppedWidth =
    cropSide === 'left' || cropSide === 'right'
      ? LOGICAL_WIDTH * normalizedCropFraction
      : LOGICAL_WIDTH;
  const croppedHeight =
    cropSide === 'top' || cropSide === 'bottom'
      ? logicalHeight * normalizedCropFraction
      : logicalHeight;
  const sourceX = cropSide === 'right' ? LOGICAL_WIDTH - croppedWidth : 0;
  const sourceY = cropSide === 'bottom' ? logicalHeight - croppedHeight : 0;
  const displayAspectRatio = croppedWidth / croppedHeight;

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) {
      return;
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const scale = canvas.width / croppedWidth;
    ctx.setTransform(scale, 0, 0, scale, -sourceX * scale, -sourceY * scale);
    renderStrokes(
      ctx,
      state.currentStroke
        ? [...state.strokes, state.currentStroke]
        : state.strokes
    );
  }, [croppedWidth, sourceX, sourceY, state]);

  // Keep the bitmap matched to display size * devicePixelRatio
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.round(width * dpr));
      canvas.height = Math.max(1, Math.round(height * dpr));
      redraw();
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [redraw]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  return (
    <canvas
      ref={canvasRef}
      className={cn(
        'w-full rounded-lg ring-1 ring-foreground/10 select-none',
        className
      )}
      style={{
        aspectRatio: displayAspectRatio,
        backgroundColor: CANVAS_BACKGROUND_COLOR,
      }}
    />
  );
};
